import { useEffect, useRef, useState } from 'react';
import { Clock, X } from 'lucide-react';
import { Popover } from '../tasks/linear/Popover';
import { useI18n } from '../../contexts/I18nContext';

/**
 * Themed replacement for `<input type="time">`, the twin of `DateField`.
 *
 * The native time picker is browser chrome (a grey two-column wheel on
 * macOS) that no CSS reaches, so it looked foreign next to the app's date
 * picker. This renders the same `HH:MM` contract through the shared
 * `Popover`, with the date picker's trigger, panel, radius and selection
 * colours: an hour column and a minute column, the chosen values filled with
 * the primary colour, today's "now" outlined like today in the calendar.
 */

interface TimeFieldProps {
  /** `HH:MM` (24h), or '' when unset. */
  value: string;
  onChange: (value: string) => void;
  /** Minute step in the list (default 5). The current value always shows. */
  step?: number;
  disabled?: boolean;
  placeholder?: string;
  /** Hides the clear button for fields that must always hold a time. */
  required?: boolean;
  id?: string;
  className?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

function parse(value: string): { h: number; m: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return null;
  return { h: Number(match[1]), m: Number(match[2]) };
}

export function TimeField({
  value,
  onChange,
  step = 5,
  disabled,
  placeholder,
  required,
  id,
  className = '',
}: TimeFieldProps) {
  const { t } = useI18n();
  const label = parse(value) ? value : (placeholder ?? t('timePicker.placeholder'));

  return (
    <Popover
      width={200}
      matchTriggerWidth
      trigger={({ open, toggle, ref }) => (
        <button
          id={id}
          ref={ref}
          type="button"
          disabled={disabled}
          onClick={toggle}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={`ui-select-trigger ${className}`}
        >
          <span className={value ? 'truncate tabular-nums' : 'truncate text-gray-400'}>
            {label}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {value && !required && !disabled && (
              // A span, not a button: the trigger is already a button.
              <span
                role="button"
                tabIndex={0}
                aria-label={t('timePicker.clear')}
                className="rounded p-0.5 text-gray-400 hover:bg-accent hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange('');
                  }
                }}
              >
                <X size={13} />
              </span>
            )}
            <Clock size={14} className="text-gray-400" />
          </span>
        </button>
      )}
    >
      {(close) => <TimePanel value={value} step={step} onChange={onChange} onDone={close} />}
    </Popover>
  );
}

/** The two columns on their own, for callers that already own a panel. */
export function TimePanel({
  value,
  step = 5,
  onChange,
  onDone,
}: {
  value: string;
  step?: number;
  onChange: (value: string) => void;
  onDone?: () => void;
}) {
  const { t } = useI18n();
  const now = new Date();
  const current = parse(value);
  // A pick in one column keeps the other; an empty field starts from now.
  const [hour, setHour] = useState(current?.h ?? now.getHours());
  const [minute, setMinute] = useState(current?.m ?? Math.floor(now.getMinutes() / step) * step);

  const minutes: number[] = [];
  for (let m = 0; m < 60; m += step) minutes.push(m);
  if (!minutes.includes(minute)) minutes.push(minute);
  minutes.sort((a, b) => a - b);

  const hourList = useRef<HTMLDivElement>(null);
  const minuteList = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Open with the chosen values in view, like the calendar opens on its month.
    for (const list of [hourList.current, minuteList.current]) {
      const selected = list?.querySelector<HTMLElement>('[aria-selected="true"]');
      if (list && selected) list.scrollTop = selected.offsetTop - list.clientHeight / 2 + 16;
    }
  }, []);

  function commit(h: number, m: number) {
    onChange(`${pad(h)}:${pad(m)}`);
  }

  const cell = (selected: boolean, isNow: boolean) =>
    [
      'h-8 w-full shrink-0 rounded text-sm tabular-nums transition-colors',
      selected
        ? 'bg-primary font-semibold text-primary-foreground'
        : 'text-foreground hover:bg-accent',
      !selected && isNow ? 'ring-1 ring-inset ring-ring' : '',
    ].join(' ');

  return (
    <div className="p-2">
      <div className="grid grid-cols-2 gap-1 px-1 pb-1">
        <span className="text-center text-[11px] font-medium uppercase text-gray-400">
          {t('timePicker.hour')}
        </span>
        <span className="text-center text-[11px] font-medium uppercase text-gray-400">
          {t('timePicker.minute')}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1">
        <div ref={hourList} className="flex max-h-56 flex-col gap-0.5 overflow-y-auto pr-0.5">
          {Array.from({ length: 24 }, (_, h) => (
            <button
              key={h}
              type="button"
              aria-selected={h === hour}
              onClick={() => {
                setHour(h);
                commit(h, minute);
              }}
              className={cell(h === hour, h === now.getHours())}
            >
              {pad(h)}
            </button>
          ))}
        </div>
        <div ref={minuteList} className="flex max-h-56 flex-col gap-0.5 overflow-y-auto pr-0.5">
          {minutes.map((m) => (
            <button
              key={m}
              type="button"
              aria-selected={m === minute}
              onClick={() => {
                setMinute(m);
                commit(hour, m);
                onDone?.();
              }}
              className={cell(m === minute, false)}
            >
              {pad(m)}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-2 border-t border-border pt-2">
        <button
          type="button"
          onClick={() => {
            const h = now.getHours();
            const m = now.getMinutes();
            setHour(h);
            setMinute(m);
            commit(h, m);
            onDone?.();
          }}
          className="w-full rounded px-2 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
        >
          {t('timePicker.now')}
        </button>
      </div>
    </div>
  );
}
